import {
  AfterContentInit,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { UUID } from "angular2-uuid";
import { includes, uniqBy } from "lodash";
import { Checkbox } from "primeng/checkbox";
import { Table } from "primeng/table";
import { forkJoin, Observable, of, Subject, throwError } from "rxjs";
import { catchError, filter, first, take, tap } from "rxjs/operators";
import { TokenService } from "src/app/auth/token.service";
import { BuildingGroupSelectComponent } from "src/app/core/building-group/building-group-select/building-group-select.component";
import { RemoveGroupFromBuildingsModalComponent } from "src/app/core/building-group/remove-group-from-buildings-modal/remove-group-from-buildings-modal.component";
import { CreateBuildingModalComponent } from "src/app/core/create-building-modal/create-building-modal.component";
import { DeviceCheckService } from "src/app/core/survey/shared/services/device-check.service";
import { BuildingSurveyViewModel } from "src/app/shared/Models/buildingTableViewModel";
import { AddressTypeEnum } from "src/app/shared/Models/enums/AddressTypeEnum";
import { BuildingTypeEnum } from "src/app/shared/Models/enums/BuildingTypeEnum";
import { SurveyStatus } from "src/app/shared/Models/enums/SurveySurveyStatus";
import { CreateInsideSurveyDTO } from "src/app/shared/Models/write/createInsideSurveyDTO";
import { CreateOutsideSurveyDto } from "src/app/shared/Models/write/CreateOutsideSurveyDto";
import { DeleteAddressDTO } from "src/app/shared/Models/write/DeleteAddressDTO";
import { RemoveGroupFromBuildingsDto } from "src/app/shared/Models/write/RemoveGroupFromBuildingsDto";
import {
  DialogService,
  DialogType,
} from "src/app/shared/services/dialog.service";
import {
  Figures,
  RandomFigureGeneratorService,
} from "src/app/shared/services/figure.service";
import { InsideSurveyService } from "src/app/shared/services/inside-survey.service";
import { OutsideSurveyService } from "src/app/shared/services/outside-survey.service";
import { StorageService } from "src/app/shared/storage.service";
import { ToastService } from "src/app/shared/toast.service";
import { NavAction } from "src/app/shared/top-nav/top-nav.component";
import { BuildingService } from "../../services/building.service";
import { AuditLogModalComponent } from "../audit-log-modal/audit-log-modal.component";
import { ColumnTypes } from "../../../../shared/Models/enums/ColumnTypeEnum";
import { FilterableTableComponent } from "../../../../shared/table/filterable-table.component";
import { User } from "../../../../auth/models/user";
import { UserService } from "../../../../auth/user.service";
import { ChangeBlockModalComponent } from "../change-block-modal/change-block-modal.component";
import { TsaRemovalService } from "src/app/shared/tsa-removal.service";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";
import { Area } from "src/app/shared/Models/read";
import { LoaderService } from "src/app/shared/services/loader.service";

const BUILDING_LIST_FILTER_STORAGE_KEY = "buildingListFilterStorage";

@Component({
  selector: "app-building-table",
  templateUrl: "./building-table.component.html",
  styleUrls: ["./building-table.component.scss"],
})
export class BuildingTableComponent
  extends FilterableTableComponent<BuildingSurveyViewModel>
  implements OnInit, AfterContentInit
{
  searchEnabled = false;
  buildingFilterValue: string = "";
  allRowsSelected: boolean;
  block: Area;
  selectedBuildings: BuildingSurveyViewModel[] = [];
  addressTable: BuildingSurveyViewModel[] = [];
  originBuildingTableData: BuildingSurveyViewModel[] = [];
  showReassignAddress = false;
  showReassignSurveyor = false;
  selectedAddressToReassign: BuildingSurveyViewModel;
  BuildingTypeEnum = BuildingTypeEnum;
  AddressTypeEnum = AddressTypeEnum;
  surveyStatus = SurveyStatus;
  clickTimeOut: boolean = false;
  lastSurveyIndex;
  users: User[] = [];
  busyValidating: boolean = false;

  scrollOffsetLandscape: number = 105;
  scrollOffsetPortrait: number = 105;

  allCols: any[] = [
    {
      field: "buildingGroupId",
      header: $localize`:@@general.buildinggroup.short:BG`,
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "buildingGroup",
      transform: (data: BuildingSurveyViewModel) => data.figure,
      filterSearch: false,
      hasEmptyValue: true,
    },
    {
      field: "buildingLomKey",
      header: "BID",
      visibleOnPortrait: false,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
      ignoredFilterValues: [0],
    },
    {
      field: "addressLomKey",
      header: "GAID",
      visibleOnPortrait: false,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
      ignoredFilterValues: [0],
    },
    {
      field: "street",
      header: $localize`:@@general.street:Street`.toUpperCase(),
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "number",
      header: "NR",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "suffix",
      header: $localize`:@@general.suffix:Suffix`.toUpperCase(),
      visibleOnPortrait: true,
      hasFilter: false,
    },
    {
      field: "outsideStartTime",
      header: $localize`:@@general.starttime:Start time`.toUpperCase(),
      visibleOnPortrait: false,
      hasFilter: true,
      type: ColumnTypes.DATE,
      template: "default",
      transform: (data: BuildingSurveyViewModel) =>
        data?.outsideStartTime
          ? new Date(data.outsideStartTime).toLocaleDateString(undefined, {
              month: "short",
              day: "2-digit",
              year: "numeric",
            })
          : undefined,
      filterSearch: true,
      hasEmptyValue: true,
    },
    {
      field: "outsideEngineer.firstName",
      header: $localize`:@@general.engineer:Engineer`.toUpperCase(),
      visibleOnPortrait: false,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "buildingType",
      header: "TYPE",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      transform: (data: BuildingSurveyViewModel) =>
        BuildingTypeEnum[data.buildingType],
      filterSearch: true,
      hasEmptyValue: true,
    },
    {
      field: "isMDU",
      header: $localize`:@@general.mdu: MDU`,
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.BOOLEAN,
      template: "isMDU",
      filterSearch: false,
      hasEmptyValue: true,
      ignoredFilterValues: [false],
    },
    {
      field: "surveyStatus",
      header: "STATUS",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "surveyStatus",
      filterSearch: false,
      hasEmptyValue: false,
    },
    {
      field: "hiddenData",
      header: "hiddenData",
      visibleOnPortrait: true,
      hasFilter: false,
      hidden: true,
    },
  ];

  @ViewChild("surveyStatus") surveyStatusTemplate: TemplateRef<{
    data: SurveyStatus;
  }>;
  @ViewChild("isMDU") isMDUTemplate: TemplateRef<{
    data: boolean;
  }>;
  @ViewChild("buildingGroup") buildingGroupTemplate: TemplateRef<{
    data: { Figure: Figures; Color: string };
  }>;
  @ViewChild("dt") table: Table;

  constructor(
    private _router: Router,
    private tokenService: TokenService,
    private _buildingService: BuildingService,
    private _storageService: StorageService,
    private _figureService: RandomFigureGeneratorService,
    private insideSurveyService: InsideSurveyService,
    private outsideSurveyService: OutsideSurveyService,
    private dialogService: DialogService,
    private readonly modalService: NgbModal,
    toastService: ToastService,
    deviceCheckService: DeviceCheckService,
    private tsaRemovalService: TsaRemovalService,
    private permissionsService: PermissionsService,
    private loaderService: LoaderService
  ) {
    super(toastService, deviceCheckService);
  }

  ngOnInit() {
    super.ngOnInit();
    // checking whether areaId is already set or not
    if (
      sessionStorage.getItem("block") &&
      sessionStorage.getItem("block") != "null"
    ) {
      this.block = JSON.parse(sessionStorage.getItem("block"));
      this.getBuildingListByAreaId();
    }
    this.initPersistence(BUILDING_LIST_FILTER_STORAGE_KEY);
    this.navActions = this.loadNavActions();
  }

  navActions: NavAction[] = [];

  ngAfterContentInit(): void {
    this.tableData$()
      .pipe(
        filter((data) => !!data.length),
        first()
      )
      .subscribe((data) => this.scrollToLastSurvey());
  }

  private scrollToLastSurvey() {
    const index = sessionStorage.getItem("lastSurveyIndex");
    sessionStorage.removeItem("lastSurveyIndex");
    if (index !== null && this.tableValues.length) {
      this.lastSurveyIndex = Number(index);

      setTimeout(() => {
        this.scrollTable(this.lastSurveyIndex);
      }, 500);
    }
  }

  scrollTable(rowIndex: number) {
    var wrapper;
    if (
      this.table.el.nativeElement.children[0].getElementsByClassName(
        "p-datatable-scrollable-body"
      )[0]
    )
      wrapper = this.table.el.nativeElement.children[0].getElementsByClassName(
        "p-datatable-scrollable-body"
      )[0];

    if (!wrapper) return;

    let rows = wrapper.getElementsByClassName("p-row");

    // No need to scroll if there aren't any products
    if (!rows.length) return;

    let rowEl = rows[rowIndex];

    // Scroll all the way to top if first item
    if (rowEl === rows[0]) wrapper.scrollTop = 0;

    // Change scroll position if not first item and at bottom of scrollbar
    if (rowEl.offsetTop + rowEl.offsetHeight > wrapper.offsetHeight) {
      // Calculate offset to make item appear at the top of the table
      var heightOffset =
        this.table.el.nativeElement.offsetHeight - rowEl.offsetHeight * 2;

      wrapper.scrollTop +=
        rowEl.offsetTop +
        rowEl.offsetHeight -
        wrapper.scrollTop -
        wrapper.offsetHeight +
        heightOffset;
    }
  }

  loadNavActions(): NavAction[] {
    let list: NavAction[] = [];
    if (
      this.selectedBuildings.length > 0 &&
      !this.selectedBuildings.some((e) => e.hasOngoingValidateSurvey) &&
      this.permissionsService.allowed(PermissionsEnum.RefreshBuilding)
    )
      list.push({
        icon: "refresh",
        onClick: () => this.refreshBuildings(),
        style: "background: #042843; color: white",
        tooltip: "Refresh building from LOM/IFH",
      });
    if (
      this.selectedBuildings.length > 0 &&
      !this.block.handoverMode &&
      this.permissionsService.allowed(PermissionsEnum.MoveBuildingToBlock)
    )
      list.push({
        icon: "shuffle",
        onClick: () => this.openChangeBlockModal(),
        style: "background: #042843; color: white",
        tooltip: "Move building to different block",
      });
    if (
      this.selectedBuildings.length > 0 &&
      !this.selectedBuildings.some((e) => e.hasOngoingValidateSurvey) &&
      this.permissionsService.allowed(
        PermissionsEnum.RemoveBuildingsFromBuildingGroup
      )
    )
      list.push({
        icon: "domain_disabled",
        onClick: () => this.openDeleteGroupFromBuildings(),
        style: "background: #042843; color: white",
        tooltip: "Remove buildings from group",
      });

    if (
      this.block &&
      this.selectedBuildings.length > 0 &&
      !this.selectedBuildings.some((e) => e.hasOngoingValidateSurvey) &&
      this.permissionsService.allowed(
        PermissionsEnum.AddBuildingsToBuildingGroup
      )
    )
      list.push({
        icon: "domain_add",
        onClick: () => this.openAddBuildingToGroupModal(),
        style: "background: #042843; color: white",
        tooltip: "Add building to group",
      });

    if (
      this.block &&
      this.permissionsService.allowed(PermissionsEnum.CreateBuilding)
    )
      list.push({
        icon: "domain_add",
        onClick: () => this.openCreateBuildingModal(),
        tooltip: "Create building",
      });
    if (
      this.permissionsService.allowed(PermissionsEnum.ValidateSurvey) &&
      !this.selectedBuildings.some((e) => e.hasOngoingValidateSurvey)
    )
      list.push({
        icon: "done",
        onClick: () => this.validateSurveys(),
        style: "background: #5aea99; color: white",
        tooltip: "Validate selected",
      });
    return list;
  }

  refreshBuildings() {
    this.loaderService.show();
    this.selectedBuildings.forEach((building) => {
      this._buildingService
        .refreshBuilding(building.buildingId)
        .pipe(first())
        .subscribe(
          () => {
            this.loaderService.hide();
            this.toastService.showSuccesToast(
              "Refresh Started",
              "Refresh for building started"
            );
            this.clearSelectedBuildings();
          },
          (_) => {
            this.loaderService.hide();
          }
        );
    });
  }

  openChangeBlockModal() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    if (this.selectedBuildings.length > 0) {
      const modalRef = this.modalService.open(ChangeBlockModalComponent);
      modalRef.componentInstance.buildings = this.selectedBuildings;

      modalRef.result.then(() => this.getBuildingListByAreaId());
    }
  }

  openAddBuildingToGroupModal() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    if (this.block && this.selectedBuildings.length > 0) {
      const modalRef = this.modalService.open(BuildingGroupSelectComponent);
      modalRef.componentInstance.areaId = this.block.id;
      modalRef.componentInstance.buildings = this.selectedBuildings;
      modalRef.componentInstance.reloadBuildingList
        .asObservable()
        .pipe(first())
        .subscribe(() => {
          this.getBuildingListByAreaId().add(
            () => (this.navActions = this.loadNavActions())
          );
        });

      modalRef.componentInstance.passEntry
        .pipe(first())
        .subscribe((data: BuildingSurveyViewModel[]) => {
          const buildingIds = (data || []).map((d) => d.buildingId);
          this.onBuildingsAddedToGroup(buildingIds);
        });
    }
  }

  openCreateBuildingModal() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    if (this.block) {
      const modalRef = this.modalService.open(CreateBuildingModalComponent);
      modalRef.componentInstance.areaId = this.block.id;
      modalRef.componentInstance.reloadBuildingList
        .asObservable()
        .pipe(first())
        .subscribe(() => this.getBuildingListByAreaId());

      modalRef.componentInstance.passEntry
        .pipe(first())
        .subscribe((data: BuildingSurveyViewModel) =>
          this.onBuildingCreated(data)
        );
    }
  }

  openDeleteGroupFromBuildings() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    if (this.selectedBuildings.length > 0) {
      const modalRef = this.modalService.open(
        RemoveGroupFromBuildingsModalComponent
      );
      modalRef.componentInstance.buildings = this.selectedBuildings;
      modalRef.componentInstance.reloadBuildingList
        .asObservable()
        .pipe(first())
        .subscribe(() => this.getBuildingListByAreaId());
      modalRef.componentInstance.passEntry
        .pipe(first())
        .subscribe(
          (removeGroupFromBuildingsDto: RemoveGroupFromBuildingsDto) => {
            this.onGroupRemovedFromBuildings(
              removeGroupFromBuildingsDto.buildingIds
            );
          }
        );
    }
  }

  onUpdateAreaId(selectedBlock: Area) {
    this.block = selectedBlock;
    this.selectedBuildings = [];
    this.allRowsSelected = false;
    this.lastSurveyIndex = null;
    this.navActions = this.loadNavActions();
    this.addressTable = [];
    this.tableValues = [];
    this.clearAllFilter();

    if (selectedBlock) this.getBuildingListByAreaId();
  }

  getBuildingListByAreaId() {
    sessionStorage.setItem("block", JSON.stringify(this.block)); // saving areaId

    return this._buildingService
      .getBuildingListByAreaId(this.block.id)
      .pipe(first())
      .subscribe(
        (res: BuildingSurveyViewModel[]) => {
          this.addressTable = this._buildingService.getSortedAddressTable(res);

          this.originBuildingTableData = uniqBy(
            this.addressTable,
            "buildingId"
          );

          this.originBuildingTableData.forEach((vm) => {
            vm.hiddenData = JSON.stringify(
              this.addressTable
                .filter((address) => address.buildingId == vm.buildingId)
                .map((nestedVm) => ({
                  buildingLomKey: nestedVm.buildingLomKey,
                  street: nestedVm.street,
                  number: nestedVm.number,
                  addressLomKey: nestedVm.addressLomKey,
                  suffix: nestedVm.suffix,
                }))
            );
          });
          this._figureService.MapFigureToBuildingGroup(
            this.originBuildingTableData
          );
          this.selectedBuildings = [];
          this.allRowsSelected = false;
          this.navActions = this.loadNavActions();
          this.tableValues = [...this.originBuildingTableData];
          this.tableValues.forEach((vm) => {
            if (vm.isPrefill == true && vm.outsideEngineer == null)
              vm.surveyStatus = SurveyStatus.Prefill;
          });
          this.globalFilter = "";
        },
        (err) => {
          this.toastService.showErrorToast(
            $localize`:@@general.error:Something went wrong`,
            $localize`:@@validation.survey.geterror:Something went wrong while retrieving the data.`
          );
        }
      );
  }

  getFullBuildingSurveyList() {
    this._buildingService
      .getBuildingSurveyList()
      .pipe(first())
      .subscribe(
        (res) => {
          this.addressTable = res;
        },
        (err) => {
          this.toastService.showErrorToast(
            $localize`:@@general.error:Something went wrong`,
            $localize`:@@validation.survey.geterror:Something went wrong while retrieving the data.`
          );
        }
      );
  }

  ChildSearchEnabled(eventSrc: any) {
    if (eventSrc.srcElement.alt == "search")
      this.searchEnabled = !this.searchEnabled;
  }

  selectAllBuildings(checkbox: Checkbox) {
    // this will select all available buildings, not the buildings per view, as I need to manipulate View otherwise. Story unclear.
    this.allRowsSelected = checkbox.checked();
    if (checkbox.checked()) {
      this.tableValues.forEach((s) => (s.isChecked = true));
      this.selectedBuildings = this.tableValues;
    } else {
      this.clearSelectedBuildings();
    }
    this.navActions = this.loadNavActions();
  }

  selectBuilding(tableRowObj: BuildingSurveyViewModel, checkbox: Checkbox) {
    if (checkbox.checked()) {
      this.selectedBuildings.push(tableRowObj);
    } else {
      this.selectedBuildings = this.selectedBuildings.filter(
        (b) => b.buildingId !== tableRowObj.buildingId
      );
      if (this.allRowsSelected) this.allRowsSelected = false;
    }
    this.navActions = this.loadNavActions();
  }

  onCustomDblClickToPreventMobileIssues(building: BuildingSurveyViewModel) {
    if (!this.clickTimeOut) {
      this.clickTimeOut = true;
      setTimeout(() => {
        this.clickTimeOut = false;
      }, 200);
    } else {
      if (
        building.surveyStatus == SurveyStatus.OutsidePending ||
        building.surveyStatus == SurveyStatus.InsidePending
      )
        this.onStartSurvey(building);
      else this.onGoToSurvey(building);
    }
  }

  onStartSurvey(
    building: BuildingSurveyViewModel,
    tableIndex = null,
    prefill = false
  ) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    if (!this.permissionsService.allowed(PermissionsEnum.StartSurvey)) {
      let PH = this.tokenService.getRoles()[0];
      this.toastService.showErrorToast(
        $localize`:@@validation.survey.cantstartsurvey:Can\'t start survey!`,
        $localize`:@@validation.survey.viewercantstartsurvey:${PH}:INTERPOLATION: is not allowed to start a Survey.`
      );
      return;
    }

    if (
      (building.hasFailedValidateSurvey || building.hasOngoingValidateSurvey) &&
      !this.permissionsService.allowed(PermissionsEnum.ForceStartSurvey)
    ) {
      let state = building.hasFailedValidateSurvey ? "failed" : "ongoing";
      this.toastService.showErrorToast(
        $localize`:@@validation.survey.cantstartsurvey:Can\'t start survey!`,
        $localize`:@@validation.survey.cantstartsurveywithongoingvalidation: Starting the survey is not allowed while a validation is ${state}.`
      );
      return;
    }

    const userId = this.tokenService.getAuthUserId();
    let func: Observable<any>;
    let surveyStatus;
    const surveyId = UUID.UUID();

    const createInside = () => {
      if (!building.allInGroupReadyToInside) func = of(false);
      surveyStatus = SurveyStatus.InsideOnGoing;
      func = this.insideSurveyService.createInsideSurvey(
        new CreateInsideSurveyDTO({
          buildingId: building.buildingId,
          insideEngineerId: userId,
          insideSurveyId: surveyId,
          buildingGroupId: building.buildingGroupId,
        })
      );
    };

    const createOutside = () => {
      surveyStatus = SurveyStatus.OutsideOnGoing;
      if (prefill == null || prefill == false) {
        prefill = false;
        func = this._buildingService.createOutsideSurvey(
          new CreateOutsideSurveyDto({
            surveyId,
            buildingId: building.buildingId,
            outsideEngineerId: userId,
            isPrefill: false,
          })
        );
      } else {
        prefill = true;
        func = this._buildingService.createOutsideSurvey(
          new CreateOutsideSurveyDto({
            surveyId,
            buildingId: building.buildingId,
            outsideEngineerId: null,
            isPrefill: true,
          })
        );
      }
    };

    switch (building.surveyStatus) {
      case SurveyStatus.OutsideDone:
      case SurveyStatus.OutsideOnGoing:
      case SurveyStatus.OutsideWaitingForValidation:
      case SurveyStatus.OutsidePending:
        createOutside();
        break;
      case SurveyStatus.InsideDone:
      case SurveyStatus.InsideOnGoing:
      case SurveyStatus.InsidePending:
        createInside();
        break;
      default:
        break;
    }

    func.pipe(first()).subscribe(
      () => {
        sessionStorage.setItem("lastSurveyIndex", tableIndex);
        this._router.navigate(["/survey", surveyStatus, surveyId, prefill]);
      },
      (error) => {
        if (error.error.code === "CantCreateInsideSurvey")
          this.toastService.showErrorToast(
            $localize`:@@validation.survey.cantstartsurvey:Can\'t start survey!`,
            $localize`:@@validation.survey.buildinggroupoutsidenotfinished:Building group has buildings with not finished outside surveys.`
          );
        else
          this.toastService.showErrorToast(
            $localize`:@@validation.survey.cantstartsurvey:Can\'t start survey!`,
            $localize`:@@validation.survey.startsomethingwentwrong:Something went wrong while trying to start the survey.`
          );

        this._router.navigateByUrl("/building-table");
      }
    );
  }

  onGoToSurvey(
    building: BuildingSurveyViewModel,
    tableIndex = null,
    prefill = null
  ) {
    if (prefill == null) building.isPrefill = false;

    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    if (!this.permissionsService.allowed(PermissionsEnum.ViewSurvey)) return;

    var surveyId;
    switch (building.surveyStatus) {
      case SurveyStatus.OutsideDone:
      case SurveyStatus.OutsideOnGoing:
      case SurveyStatus.OutsideWaitingForValidation:
      case SurveyStatus.OutsidePending:
        surveyId = building.surveyId;
        break;
      case SurveyStatus.InsideDone:
      case SurveyStatus.InsideOnGoing:
      case SurveyStatus.InsidePending:
      case SurveyStatus.InsideWaitingForValidation:
        if (!building.allInGroupReadyToInside) {
          this.toastService.showErrorToast(
            $localize`:@@validation.survey.cantstartsurvey:Can\'t start survey!`,
            $localize`:@@validation.survey.buildinggroupoutsidenotfinished:Building group has buildings with not finished outside surveys.`
          );
          this._router.navigateByUrl("/building-table");
          return;
        }
        surveyId = building.insideSurveyId;
        break;
      case SurveyStatus.Prefill:
        surveyId = building.surveyId;
        break;
      default:
        break;
    }

    sessionStorage.setItem("lastSurveyIndex", tableIndex);
    this._router.navigate([
      "/survey",
      building.surveyStatus,
      surveyId,
      building.isPrefill,
    ]);
  }

  displaySurveyStatus(data: BuildingSurveyViewModel): BuildingSurveyViewModel {
    if (data.surveyStatus == SurveyStatus.OutsideOnGoing && data.isPrefill)
      data.surveyStatus = this.surveyStatus.Prefill;
    return data;
  }

  canOpenOutsideSurvey(surveyStatus: SurveyStatus) {
    return surveyStatus == SurveyStatus.InsidePending;
  }

  canAssingSurveyToMe(building: BuildingSurveyViewModel) {
    if (building.hasOngoingValidateSurvey) return false;

    return (
      building.surveyStatus != SurveyStatus.OutsidePending &&
      this.permissionsService.allowed(PermissionsEnum.AssignSurveyToMe)
    );
  }

  canPrefillOutsideSurvey(building: BuildingSurveyViewModel) {
    if (building.hasOngoingValidateSurvey) return false;

    if (this.permissionsService.allowed(PermissionsEnum.PrefillSurvey))
      if (
        building.surveyStatus == SurveyStatus.OutsidePending ||
        (building.surveyStatus == SurveyStatus.OutsideOnGoing &&
          building.isPrefill) ||
        building.surveyStatus == SurveyStatus.Prefill
      )
        return true;
      else return false;
    else return false;
  }

  prefillOutsideSurvey(building: BuildingSurveyViewModel, tableIndex = null) {
    building.isPrefill = true;
    if (building.surveyId == null)
      this.onStartSurvey(building, tableIndex, true);
    else this.onGoToSurvey(building, tableIndex, true);
  }

  openOutsideSurvey(building: BuildingSurveyViewModel) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    if (!this.permissionsService.allowed(PermissionsEnum.ViewSurvey)) return;

    this._router.navigate([
      "/survey",
      SurveyStatus.OutsideDone,
      building.surveyId,
      building.isPrefill,
    ]);
  }

  validateSurveys() {
    if (!this.busyValidating) {
      this.busyValidating = true;
      setTimeout(() => {
        this.busyValidating = false;
      }, 200);

      if (!navigator.onLine) {
        this.toastService.showWarnToast(
          $localize`:@@general.nointernet:No internet`,
          $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
        );
        return;
      }

      if (
        this.selectedBuildings.find(
          (t) => t.tsaId != null && t.surveyStatus > 3
        ) != undefined
      ) {
        this.toastService.showWarnToast(
          $localize`:@@general.buildingwithtsa:Unable to validate the surveys with TSA records`,
          $localize``
        );
        this.selectedBuildings = this.selectedBuildings.filter(
          (t) => t.tsaId == null
        );

        forkJoin(this.selectedBuildings.map((s) => this.validateSurvey(s)))
          .pipe(
            take(1),
            tap((s) => {
              var anySuccess = s.some((e) => e);

              if (anySuccess)
                this.toastService.showSuccesToast(
                  "Validation Success",
                  "The selected buildings without a TSA and that where in status 'Waiting for Validation' have been validated."
                );

              this.allRowsSelected = false;
            }),
            catchError((s) => {
              this.toastService.showErrorToast(
                "Something went wrong",
                JSON.stringify(s)
              );
              return of(Error(s));
            }),
            tap((s) => {
              this.getBuildingListByAreaId();
            })
          )
          .subscribe();
      } else {
        forkJoin(this.selectedBuildings.map((s) => this.validateSurvey(s)))
          .pipe(
            take(1),
            tap((s) => {
              var anySuccess = s.some((e) => e);

              if (anySuccess)
                this.toastService.showSuccesToast(
                  "Validation Success",
                  "All selected buildings in status 'Waiting for Validation' have been validated."
                );

              this.allRowsSelected = false;
            }),
            catchError((s) => {
              this.toastService.showErrorToast(
                "Something went wrong",
                JSON.stringify(s)
              );
              return of(Error(s));
            }),
            tap((s) => {
              this.getBuildingListByAreaId();
            })
          )
          .subscribe();
      }
    }
  }

  buildingNeedsInsideSurvey(vm: BuildingSurveyViewModel) {
    if (!!vm.buildingGroupId) return true;

    if (vm.isMDU)
      return (
        vm.buildingType !== BuildingTypeEnum.EmptyParcel &&
        vm.buildingType !== BuildingTypeEnum.NoAccess &&
        vm.buildingType !== BuildingTypeEnum.NotInUse
      );
  }

  validateSurvey(vm: BuildingSurveyViewModel) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    var isValidSurveyStatus =
      vm.surveyStatus === SurveyStatus.OutsideWaitingForValidation ||
      vm.surveyStatus === SurveyStatus.InsideWaitingForValidation;

    if (
      isValidSurveyStatus &&
      (vm.coordinates === undefined ||
        !vm.coordinates[0]?.posX ||
        !vm.coordinates[0]?.posY)
    )
      return throwError(
        "X and/or Y coordinates missing for building " + vm.buildingLomKey
      );

    switch (vm.surveyStatus) {
      case SurveyStatus.InsideWaitingForValidation:
        return this.insideSurveyService.validate({
          surveyId: vm.insideSurveyId,
          validatorRemark: "",
          deleteTSA: false,
        });
      case SurveyStatus.OutsideWaitingForValidation:
        return this.outsideSurveyService.validateSurvey({
          surveyId: vm.surveyId,
          surveyStatus: this.buildingNeedsInsideSurvey(vm)
            ? SurveyStatus.InsidePending
            : SurveyStatus.OutsideDone, // found this logic for Validation in OutsideComponent.
          coordinates: vm.coordinates || [],
          validatorRemark: "",
        });
      default:
        return of(false);
    }
  }
  assignSurveyToMe(building: BuildingSurveyViewModel, index: any) {
    if (!this.permissionsService.allowed(PermissionsEnum.AssignSurveyToMe))
      return;

    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }
    var insideSurveyId =
      (building.surveyStatus as number) >= 4 ? building.insideSurveyId : null;
    this._buildingService
      .changeEngineer(
        this.tokenService.getAuthUserId(),
        building.surveyId,
        insideSurveyId
      )
      .pipe(first())
      .subscribe((res) => {
        if (!insideSurveyId)
          this.addressTable = this.addressTable.map((r) => {
            if (r.buildingId == building.buildingId) {
              r.outsideEngineer.userId = this.tokenService.getAuthUserId();
              r.outsideEngineer.firstName =
                this.tokenService.getPayloadAttribute("firstName");
            }
            return r;
          });
      });
  }

  canGoBackToOutsideOngoing(building: BuildingSurveyViewModel) {
    if (
      !this.permissionsService.allowed(PermissionsEnum.SetSurveyBackToOngoing)
    )
      return false;
    if (
      building.hasOngoingValidateSurvey &&
      !this.permissionsService.allowed(PermissionsEnum.ForceRollBack)
    )
      return false;

    return includes(
      [
        SurveyStatus.InsidePending,
        SurveyStatus.InsideDone,
        SurveyStatus.InsideOnGoing,
        SurveyStatus.OutsideDone,
      ],
      building.surveyStatus
    );
  }

  backToOutsideOngoing(building: BuildingSurveyViewModel) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    var dialogText = this.tsaRemovalService.getRemarkForBuildingSurveyViewModel(
      building,
      $localize`:@@buildinglist.movetooutsideonging.detailtext:You're about to move this building to outside ongoing!`
    );
    this.dialogService
      .openDialog({
        detailText: dialogText,
        dialogType: DialogType.Continue,
        headerText: $localize`:@@buildinglist.movetooutsideonging.header:Move to outside ongoing`,
      })
      .pipe(first())
      .subscribe((feedback) => {
        if (feedback)
          this._buildingService
            .backSurveyToOutsideOngoing(
              building.insideSurveyId,
              building.buildingId,
              building.buildingGroupId
            )
            .pipe(first())
            .subscribe(() => this.getBuildingListByAreaId());
      });
  }

  onBuildingsAddedToGroup(ids: string[]) {
    this.clearSelectedBuildings();
    this.addressTable = this.addressTable.map((b) => {
      if (ids.includes(b.buildingId)) b.buildingGroupId = b.buildingId;
      return b;
    });
    this._figureService.MapFigureToBuildingGroup(this.addressTable);
    this.clearAllFilter();
  }

  onBuildingCreated(b: BuildingSurveyViewModel) {
    this.addressTable.unshift(b);
  }

  onGroupRemovedFromBuildings(buildingIds: string[]) {
    this.addressTable = this.addressTable.map((b) => {
      if (buildingIds.includes(b.buildingId)) {
        b.figure = null;
        b.buildingGroupId = null;
      }

      return b;
    });

    this._figureService.MapFigureToBuildingGroup(this.addressTable);
    this.clearAllFilter();
  }

  addressActionsAvailble(
    address: BuildingSurveyViewModel,
    building: BuildingSurveyViewModel,
    allAddresses: BuildingSurveyViewModel[]
  ): boolean {
    return (
      this.addressReassignable(address.buildingId) ||
      this.canAssingSurveyToMe(address) ||
      this.surveyorReassignable(address.surveyStatus) ||
      this.canGoBackToOutsideOngoing(building) ||
      this.canPrefillOutsideSurvey(address) ||
      this.canOpenOutsideSurvey(address.surveyStatus) ||
      this.addressDeletable(address, allAddresses)
    );
  }

  addressReassignable(buildingId: string) {
    const lomBuildings = this.addressTable.filter(
      (b) => b.buildingId == buildingId
    );
    return (
      lomBuildings.some((b) => SurveyStatus.OutsideDone > b.surveyStatus) &&
      !lomBuildings.some((b) => b.hasOngoingValidateSurvey) &&
      this.permissionsService.allowed(PermissionsEnum.ReassignAddress)
    );
  }

  addressDeletable(
    building: BuildingSurveyViewModel,
    addressTable: BuildingSurveyViewModel[]
  ) {
    return (
      (this.checkLastAddressOfBuilding(building, addressTable)
        ? !building.buildingLomKey && !building.addressLomKey
        : !building.addressLomKey) &&
      !building.hasOngoingValidateSurvey &&
      this.permissionsService.allowed(PermissionsEnum.DeleteAddress)
    );
  }

  deleteAddress(
    building: BuildingSurveyViewModel,
    addressTable: BuildingSurveyViewModel[]
  ) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    var detailText = this.tsaRemovalService.getRemarkForBuildingSurveyViewModel(
      building,
      $localize`:@@buildinglist.deleteaddress.detailtext:You are about to delete this address!`
    );

    this.dialogService
      .openDialog({
        headerText: $localize`:@@buildinglist.deleteaddress.header:Delete address`,
        dialogType: DialogType.Delete,
        detailText: detailText,
        warningText: this.checkForWarningText(building, addressTable),
      })
      .pipe(first())
      .subscribe((feedback) => {
        if (feedback)
          this._buildingService
            .deleteAddress(
              new DeleteAddressDTO({ addressId: building.addressId })
            )
            .pipe(first())
            .subscribe(() => {
              this.getBuildingListByAreaId();
            });
      });
  }

  checkLastAddressOfBuilding(
    building: BuildingSurveyViewModel,
    addressTable: BuildingSurveyViewModel[]
  ): boolean {
    return !addressTable.find(
      (x) =>
        x.buildingId === building.buildingId &&
        x.addressId != building.addressId
    );
  }

  checkForWarningText(
    building: BuildingSurveyViewModel,
    addressTable: BuildingSurveyViewModel[]
  ) {
    if (this.checkLastAddressOfBuilding(building, addressTable))
      return $localize`:@@buildinglist.deleteaddress.warningtext:This is the last address of the building. Deleting this address will also delete the building and all survey data.`;
    return null;
  }

  reassignAddress(building: BuildingSurveyViewModel) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    this.showReassignAddress = true;
    this.selectedAddressToReassign = building;
  }

  closeReassignAddress(result: { IsNewBuilding: boolean; lomkey?: string }) {
    if (result.IsNewBuilding && result.lomkey != null) {
      let addressWithLomkey = this.addressTable.filter(
        (buildingTa) => buildingTa.buildingLomKey == result.lomkey
      );
      if (addressWithLomkey.length == 1)
        addressWithLomkey[0].buildingLomKey = "";
    }
    this.showReassignAddress = false;
  }

  private updateTableStorage(data: { data: any; key: string }) {
    this._storageService.store(data.data, data.key, 21600);
  }

  showAudit(building: BuildingSurveyViewModel) {
    const opts: any = {
      size: "xl",
    };
    const modalRef = this.modalService.open(AuditLogModalComponent, opts);
    modalRef.componentInstance.building = building;
  }

  showChangeBlock(buildings: BuildingService[]) {
    const opts: any = {
      size: "xl",
    };
    const modalRef = this.modalService.open(ChangeBlockModalComponent, opts);
    modalRef.componentInstance.buildings = buildings;
  }

  canOpenEmptyBuildingList(): boolean {
    return this.permissionsService.allowed(
      PermissionsEnum.OpenEmptyBuildingsList
    );
  }

  surveyorReassignable(surveyStatus: SurveyStatus) {
    return (
      surveyStatus !== SurveyStatus.OutsideDone &&
      surveyStatus !== SurveyStatus.InsideDone &&
      surveyStatus !== SurveyStatus.InsidePending &&
      surveyStatus !== SurveyStatus.OutsidePending &&
      this.permissionsService.allowed(PermissionsEnum.ReassignSurvey)
    );
  }

  assignSurveyToSurveyor(building: BuildingSurveyViewModel) {
    if (!this.permissionsService.allowed(PermissionsEnum.ReassignSurvey))
      return;

    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    this.selectedAddressToReassign = building;
    this.showReassignSurveyor = true;
  }

  closeReassignSurveyor(buildingSurveyVms: BuildingSurveyViewModel[]) {
    this.addressTable = buildingSurveyVms;
    this.showReassignSurveyor = false;
  }

  isHighlight(index) {
    return this.lastSurveyIndex === index;
  }

  clearSelectedBuildings() {
    this.tableValues.forEach((s) => (s.isChecked = false));
    this.selectedBuildings = [];
  }
}
